import crypto from 'node:crypto';
import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { git } from '../lib/git.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';
import { autoCommit, ensureGitReady } from './deploy.js';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const POLL_MS = 5_000;
const PREVIEW_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * `ghosty preview` — put the current work on a live preview URL without
 * touching production. Pushes the working tree to a branch, asks the platform
 * for that branch's preview (find-or-create; the push/PR webhook is what
 * actually builds it), then waits until the preview is live.
 */

/**
 * Branch for a fresh preview: `preview/{kebab(name)}`, or a timestamped
 * `preview/{yyyymmdd-hhmm}-{4hex}` when no usable name was given. Exported
 * for tests (`now` is injectable; the hex suffix is random).
 */
export function previewBranchName(name?: string, now: Date = new Date()): string {
  const kebab = (name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  if (kebab) return `preview/${kebab}`;
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `preview/${stamp}-${crypto.randomBytes(2).toString('hex')}`;
}

/** One row of GET /apps/:slug/previews, as the CLI reads it. */
export interface PreviewRowView {
  prNumber?: number;
  status?: string;
  url?: string;
  headSha?: string;
  error?: string;
}

export type PreviewPollOutcome =
  | { state: 'waiting' }
  | { state: 'live'; url?: string }
  | { state: 'failed'; error?: string }
  | { state: 'closed' };

/**
 * Interpret one poll of the previews list for our PR. A row whose headSha is
 * NOT the sha we just pushed is STALE and counts as waiting — a pre-existing
 * PR's row may still say `live` for the old head while the webhook for our
 * push is in flight. Only rows for OUR sha are terminal.
 */
export function previewPollState(
  row: PreviewRowView | undefined,
  pushedSha: string,
): PreviewPollOutcome {
  if (!row || row.headSha !== pushedSha) return { state: 'waiting' };
  if (row.status === 'live') return { state: 'live', url: row.url };
  if (row.status === 'failed') return { state: 'failed', error: row.error };
  if (row.status === 'closed') return { state: 'closed' };
  return { state: 'waiting' }; // building (or an unknown future status)
}

interface PreviewAccepted {
  ok: boolean;
  prNumber: number;
  previewUrl: string;
  prUrl: string;
  created: boolean;
}

export async function runPreview(
  name: string | undefined,
  options: { dir?: string; json?: boolean; adminUrl?: string },
): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  try {
    config = readAppConfig(dir);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }
  const slug = config.slug;

  // ---- git: put the work on a preview branch and push it --------------------
  ensureGitReady(io, dir);

  const current = git(['branch', '--show-current'], dir)?.output.trim() ?? '';
  let branch: string;
  if (!current || current === 'main') {
    // On main (or detached): previews never come from main — cut a branch.
    branch = previewBranchName(name);
    const switched = git(['switch', '-c', branch], dir);
    if (!switched?.ok) {
      fail(
        io,
        `Couldn't create the preview branch "${branch}": ${switched?.output ?? 'unknown error'}`,
        1,
        'BRANCH_FAILED',
      );
    }
    note(io, `Created preview branch ${branch}.`);
  } else {
    // Already on a branch: that IS the preview — a name would fork the work.
    branch = current;
    if (name) note(io, `Already on branch "${branch}" — previewing it (the name "${name}" was ignored).`);
  }

  autoCommit(io, dir, 'Preview');

  note(io, 'Pushing to your repo…');
  const push = git(['push', '-u', 'origin', branch], dir);
  if (!push?.ok) {
    fail(
      io,
      `git push failed: ${push?.output ?? 'unknown error'}. Make sure your GitHub account can ` +
        'push to this repo (accept the org invite from `ghosty login`, or pull remote changes first).',
      1,
      'PUSH_FAILED',
    );
  }
  const sha = git(['rev-parse', 'HEAD'], dir)?.output.trim() ?? '';
  if (io.json) emitJson({ event: 'pushed', slug, branch, commitSha: sha });

  // ---- ask the platform for this branch's preview ---------------------------
  const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
  let accepted: PreviewAccepted;
  try {
    accepted = await client.post<PreviewAccepted>(`/builder-api/apps/${slug}/previews`, { branch });
  } catch (err) {
    fail(io, (err as Error).message, 1, 'PREVIEW_REJECTED');
  }
  if (io.json) {
    emitJson({
      event: 'pr',
      slug,
      prNumber: accepted.prNumber,
      prUrl: accepted.prUrl,
      created: accepted.created,
    });
    emitJson({ event: 'building', slug, prNumber: accepted.prNumber });
  } else {
    note(
      io,
      accepted.created
        ? `Preview #${accepted.prNumber} opened. Building…`
        : `Updating preview #${accepted.prNumber}…`,
    );
  }

  // ---- wait until the preview for OUR commit is live ------------------------
  const deadline = Date.now() + PREVIEW_TIMEOUT_MS;
  let liveUrl = accepted.previewUrl;
  for (;;) {
    if (Date.now() > deadline) {
      fail(
        io,
        'Preview timed out after 15 minutes. See the build log in your app console → Previews.',
        1,
        'PREVIEW_TIMEOUT',
      );
    }
    await sleep(POLL_MS);
    let row: PreviewRowView | undefined;
    try {
      const { items } = await client.get<{ items: PreviewRowView[] }>(
        `/builder-api/apps/${slug}/previews`,
      );
      row = items.find((r) => r.prNumber === accepted.prNumber);
    } catch {
      continue; // transient; keep polling
    }
    const state = previewPollState(row, sha);
    if (state.state === 'waiting') continue;
    if (state.state === 'live') {
      liveUrl = state.url ?? liveUrl;
      break;
    }
    if (state.state === 'closed') {
      fail(
        io,
        'This preview was closed before it finished. Run `ghosty preview` again to start a new one.',
        1,
        'PREVIEW_FAILED',
      );
    }
    const detail = state.error?.trim()
      ? `\n${state.error
          .trim()
          .split('\n')
          .map((line) => `  ${line}`)
          .join('\n')}\n`
      : ' ';
    fail(
      io,
      `Preview build failed.${detail}See the full build log in your app console → Previews.`,
      1,
      'PREVIEW_FAILED',
    );
  }

  // ---- done ------------------------------------------------------------------
  if (io.json) {
    emitJson({
      ok: true,
      slug,
      prNumber: accepted.prNumber,
      previewUrl: liveUrl,
      prUrl: accepted.prUrl,
    });
    return;
  }
  human(io);
  ok(io, 'Preview is live.');
  human(io, `  Try it: ${pc.bold(liveUrl)}`);
  human(io, `  Pull request: ${accepted.prUrl}`);
  human(io, '  Push more work to this branch to update it; `ghosty deploy` ships it for real.');
  human(io);
}
